import { useEffect } from "react";
import { AnimatePresence } from "framer-motion";

import "../bingo.css";
import { useHotelStore } from "@/core/state/hotelStore";
import { useSettingsStore } from "@/core/state/settingsStore";
import { audioService } from "@/core/audio/audioService";
import {
  GameStatus,
  useBingoGameStore,
} from "@/plugins/bingo/state/bingoGameStore";
import { attachProjectorReceiver } from "@/plugins/bingo/state/projectorReceiver";

import IdleWelcome from "./IdleWelcome";
import CinematicTransition from "./CinematicTransition";
import InGameView from "./InGameView";
import WinnerOverlayBasic from "./WinnerOverlayBasic";
import GameSummaryBasic from "./GameSummaryBasic";
import BingoCheckLiveOverlay from "@/plugins/bingo/verification/BingoCheckLiveOverlay";
import ConfirmedBingoLiveOverlay from "@/plugins/bingo/celebration/ConfirmedBingoLiveOverlay";

/**
 * ProjectorView — the audience-facing screen.
 *
 * Runs in its own browser window. Subscribes to the BroadcastChannel and
 * mirrors the Host's game state. Renders the correct scene for each
 * game status:
 *   IDLE               → IdleWelcome
 *   PRE_GAME_TRANSITION → CinematicTransition
 *   IN_PROGRESS | PAUSED | REVIEWING → InGameView
 *   ANNOUNCING_WINNER  → InGameView + WinnerOverlayBasic
 *   GAME_SUMMARY       → GameSummaryBasic
 */
export default function ProjectorView() {
  const hotel = useHotelStore((s) => s.hotel);
  const status = useBingoGameStore((s) => s.status);
  const stage = useBingoGameStore((s) => s.stage);
  const called = useBingoGameStore((s) => s.called);
  const manualMarks = useBingoGameStore((s) => s.manualMarks);
  const winners = useBingoGameStore((s) => s.winners);
  const muted = useSettingsStore((s) => s.settings.muted);

  useEffect(() => {
    const detach = attachProjectorReceiver();
    audioService.unlockOnUserGesture();
    return detach;
  }, []);

  useEffect(() => {
    audioService.setMuted(muted);
  }, [muted]);

  const winnerName =
    status === GameStatus.ANNOUNCING_WINNER
      ? winners?.[stage]?.name || null
      : null;

  return (
    <div
      data-testid="projector-view"
      data-bingo-shell
      className="ic-app ic-bingo-shell fixed inset-0 w-screen h-screen overflow-hidden"
      style={{ background: "var(--ic-bg-darkest)" }}
    >
      <AnimatePresence mode="wait">
        {status === GameStatus.IDLE && (
          <IdleWelcome key="idle" hotel={hotel} />
        )}
        {status === GameStatus.PRE_GAME_TRANSITION && (
          <CinematicTransition key="transition" />
        )}
        {(status === GameStatus.IN_PROGRESS ||
          status === GameStatus.PAUSED ||
          status === GameStatus.REVIEWING ||
          status === GameStatus.ANNOUNCING_WINNER) && (
          <InGameView
            key="in-game"
            hotel={hotel}
            status={status}
            stage={stage}
            called={called}
            manualMarks={manualMarks}
          />
        )}
        {status === GameStatus.GAME_SUMMARY && (
          <GameSummaryBasic
            key="summary"
            hotel={hotel}
            winners={winners}
            calledCount={called.length}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {status === GameStatus.ANNOUNCING_WINNER && (
          <WinnerOverlayBasic
            key="winner-overlay"
            stage={stage}
            name={winnerName}
          />
        )}
      </AnimatePresence>

      {/* Bingo Check · Heartbeat Verification — sits above everything
          else so the room naturally focuses on the verification. */}
      <BingoCheckLiveOverlay />

      {/* Confirmed Bingo Celebration — the hybrid gold-then-burst
          moment that follows a completed verification. */}
      <ConfirmedBingoLiveOverlay />
    </div>
  );
}
